import {useMemo} from 'react'
import {useInfiniteTimelinePageData} from '../../../page-data/loaders/use-timeline'
import {usePullRequestBasePath} from '../../../stores/selectors'
import type {TimelineActivityGroupKey} from '../../../contexts/DockedPanelContext'
import {TimelineItemList} from './TimelineItemList'
import {useInfiniteScrollLoad} from './use-infinite-scroll-load'
import {groupPredicate, matchesTimelineSince} from './timeline-filters'
import styles from './TimelinePanel.module.css'

interface TimelineActivityGroupProps {
  label: string
  group: TimelineActivityGroupKey
  since?: string
  expectedCount?: number
}

export function TimelineActivityGroup({label, group, since, expectedCount}: TimelineActivityGroupProps) {
  const pathName = usePullRequestBasePath()
  const {data, fetchNextPage, hasNextPage, isFetchingNextPage} = useInfiniteTimelinePageData({pathName})

  const allItems = useMemo(() => data.pages.flatMap(page => page.data), [data.pages])
  const predicate = useMemo(() => groupPredicate(group), [group])

  const filtered = useMemo(
    () =>
      allItems.filter(item => {
        if (!predicate(item)) return false
        return !since || matchesTimelineSince(item, since)
      }),
    [allItems, predicate, since],
  )

  // Keep loading pages while the group might still have matching events on later pages, but stop
  // once we've gathered the count the sidebar promised — bounds fetching to avoid pulling the whole
  // timeline when counts come from a different endpoint than the timeline data.
  const hasMore = hasNextPage && (expectedCount == null || filtered.length < expectedCount)
  const sentinel = useInfiniteScrollLoad({fetchNextPage, hasMore, isFetchingNextPage})

  return (
    <div role="region" aria-label={label}>
      <h4 className={styles.activityGroupHeading}>{label}</h4>
      <TimelineItemList
        items={filtered}
        clipSidebar={hasMore && isFetchingNextPage ? false : 'end'}
        sentinel={sentinel}
      />
    </div>
  )
}
